import { jsx as _jsx } from "react/jsx-runtime";
import { cn } from '../lib/cn';
import { organizationInitials } from '../lib/initials';
/**
 * **An organizer's logo, or their initials** — the one square that stands for an organization
 * wherever a member meets one.
 *
 * ## Why this exists
 *
 * Sumit, 2026-09-12: *"also make sure organizer logo is displayed"*. The organizer's own public
 * profile had drawn the uploaded mark since `ORG-017` and fell back to an initials tile
 * (`ORG-015` AC-2); the **event page's host row** and the **Discover card's organizer chip** drew
 * initials for everybody, because `DiscoveryHost` had nowhere to put a logo. Two of the three
 * places a member sees an organizer disagreed with the third about what that organizer looks like.
 *
 * So the fallback is a component rather than a pattern each screen repeats. Three sizes, one rule:
 * an address renders the image, anything else renders the tile.
 *
 * ## `null` covers four different facts and draws one thing
 *
 * No logo uploaded, an object that is no longer in the bucket, a key the media service refused,
 * and an organization with no profile row at all. A member cannot tell those apart and should not
 * have to — what they see is an organizer without a mark, which is the ordinary case.
 *
 * ## No layout shift
 *
 * The box is sized by `className` and the image fills it, so the square is the same size before
 * the image arrives, while it is loading, and if it never loads. `object-cover` crops rather than
 * distorting: a wide logo in a square frame is the organizer's own crop problem, and squashing it
 * would be ours.
 *
 * `alt=""` on the image, deliberately. The mark always sits beside the organizer's name — the host
 * row, the card's chip and the profile header all render it as a sibling — so alt text would make
 * a screen reader say the name twice.
 */
export function OrganizerMark({ name, logoUrl, className, shape = 'circle', }) {
    const radius = shape === 'circle' ? 'rounded-pill' : 'rounded-xl';
    const box = cn('inline-flex shrink-0 items-center justify-center overflow-hidden', radius);
    if (typeof logoUrl === 'string' && logoUrl.length > 0) {
        return (_jsx("span", { className: cn(box, className), "aria-hidden": true, children: _jsx("img", { src: logoUrl, alt: "", loading: "lazy", className: "h-full w-full object-cover" }) }));
    }
    return (_jsx("span", { className: cn(box, 'bg-spark font-brand font-bold uppercase text-white', className), "aria-hidden": true, children: organizationInitials(name) }));
}
//# sourceMappingURL=organizer-mark.js.map